import React, { useState } from 'react';
import { useRouter } from 'next/router';
import { useDebounce } from 'use-debounce';
import { MdOutlineSearch } from 'react-icons/md';
import { useTranslation } from 'react-i18next';

import OrgAdminLayout from 'screens/organizations/components/OrgAdminLayout';
import PageLayout from 'components/PageLayout';
import PageSkeleton from 'components/PageSkeleton';
import { useGetGroup } from 'legacy/groups/hooks';
import { GroupMemberDto } from 'legacy/groups/types';

import AdminsList from './components/AdminsList';
import RequestsList from './components/RequestsList';
import MembersList from './components/MembersList';
import MemberOptionsModal from './components/MemberOptionsModal';
import AssignTitleModal from './components/AssignTitleModal';

const OrganizationMembers = () => {
  const { t } = useTranslation(['organizations']);
  const { query } = useRouter();
  const organizationId = query.organizationId as string;
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, 200);
  const [selectedMember, setSelectedMember] = useState<GroupMemberDto | null>(
    null
  );

  const [assignTitleModalOpen, setAssignTitleModalOpen] = useState(false);

  const { data: organization, isLoading: isLoadingOrganization } = useGetGroup({
    groupId: organizationId
  });

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
  };

  const closeMemberOptionsModal = () => {
    setSelectedMember(() => null);
  };

  const handleAssignTitle = () => {
    setAssignTitleModalOpen(true);
  };

  if (isLoadingOrganization || !organization) {
    return <PageSkeleton />;
  }

  return (
    <OrgAdminLayout>
      <PageLayout
        isAdmin={organization?.isUserAdmin}
        isUserMember={organization?.isUserMember}
      >
        <>
          <div className="pb-10">
            <div className="relative shrink-0 flex h-12 border-1 border-gray-300 rounded-full mt-5 mb-2">
              <MdOutlineSearch
                className="absolute left-3 text-gray-400 self-center justify-center"
                size={24}
              />
              <input
                type="text"
                className="w-full h-full px-11 rounded-full text-base text-gray-600 placeholder:text-gray-400"
                placeholder={t('actions.search')}
                value={search}
                onChange={handleSearchChange}
              />
            </div>
          </div>
          <div className="flex gap-10 flex-col">
            <div className="flex flex-col-reverse md:flex-row gap-11">
              <AdminsList
                organizationId={organizationId}
                search={debouncedSearch}
                setSelectedMember={setSelectedMember}
                isUserAdmin={organization.isUserAdmin}
              />
              <RequestsList
                organizationId={organizationId}
                search={debouncedSearch}
                isAdmin={organization.isUserAdmin}
              />
            </div>
            <div className="flex flex-col md:flex-row gap-11">
              <MembersList
                organizationId={organizationId}
                search={debouncedSearch}
                setSelectedMember={setSelectedMember}
                isUserAdmin={organization.isUserAdmin}
              />
            </div>
          </div>
        </>
      </PageLayout>
      <MemberOptionsModal
        closeMemberOptionsModal={closeMemberOptionsModal}
        selectedMember={selectedMember}
        visible={!!selectedMember}
        organizationId={organizationId}
        handleAssignTitle={handleAssignTitle}
      />
      {assignTitleModalOpen && (
        <AssignTitleModal
          onClose={() => {
            setAssignTitleModalOpen(false);
            setSelectedMember(null);
          }}
          organizationId={organizationId}
          selectedMember={selectedMember}
        />
      )}
    </OrgAdminLayout>
  );
};

export default OrganizationMembers;
